import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";

// Business config record (djr-be Phase A, admin JWT routes under
// /admin/business-config). One record, published as numbered versions; the
// admin edits a draft (revision counted on every write) and publishes it.
// Contract: djr-be controllers/businessConfigController.js.
// A draft answer (PATCH, accept, dismiss) is the new draft view: put it in the
// draft cache at once, so the next save sends the new revision even before the
// refetch lands.
const putDraft = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    const { data } = await queryFulfilled;
    if (data && typeof data === "object" && Number.isInteger(data?.revision)) {
      const { msg, ...view } = data;
      dispatch(businessConfigApi.util.upsertQueryData("getBusinessConfigDraft", undefined, view));
    }
  } catch {
    // the error is handled by the caller; invalidation refetches the draft
  }
};

const TAGS = ["BusinessConfigCurrent", "BusinessConfigStatus", "BusinessConfigVersions", "BusinessConfigDraft", "BusinessConfigDiff", "BusinessConfigComposerTemplates"];

const businessConfigApi = createApi({
  reducerPath: "businessConfigApi",
  baseQuery: prepareBaseQuery("/admin/business-config"),
  tagTypes: TAGS,
  endpoints: (builder) => ({
    // { version, publishedAt, publishedBy, note, checksum, data, defaultsChecksum }
    // or { version: 0, data: null } before the first publish.
    getBusinessConfigCurrent: builder.query({
      query: () => "",
      providesTags: ["BusinessConfigCurrent"],
    }),
    // { currentVersion, lock, lastPublish, drive, db }
    getBusinessConfigStatus: builder.query({
      query: () => "/status",
      providesTags: ["BusinessConfigStatus"],
    }),
    // { items: [{ version, publishedAt, publishedBy, note, checksum, diffSummary, audit }] }, the 20 newest.
    getBusinessConfigVersions: builder.query({
      query: () => "/versions",
      providesTags: ["BusinessConfigVersions"],
    }),
    // One full version.
    getBusinessConfigVersion: builder.query({
      query: (version) => `/versions/${encodeURIComponent(String(version ?? ""))}`,
      providesTags: ["BusinessConfigVersions"],
    }),
    // { revision, updatedAt, updatedBy, baseVersion, data, proposal, validation }
    getBusinessConfigDraft: builder.query({
      query: () => "/draft",
      providesTags: ["BusinessConfigDraft"],
    }),
    // { ifRevision, set: { "<path>": value } } or { ifRevision, data }.
    patchBusinessConfigDraft: builder.mutation({
      query: ({ ifRevision, set, data } = {}) => ({
        url: "/draft",
        method: "PATCH",
        body: data !== undefined ? { ifRevision, data } : { ifRevision, set: set ?? {} },
      }),
      onQueryStarted: putDraft,
      invalidatesTags: ["BusinessConfigDraft", "BusinessConfigDiff"],
    }),
    // Copies the Cowork proposal into the draft and clears it.
    acceptBusinessConfigProposal: builder.mutation({
      query: ({ ifRevision } = {}) => ({
        url: "/draft/accept-proposal",
        method: "POST",
        body: { ifRevision },
      }),
      onQueryStarted: putDraft,
      invalidatesTags: ["BusinessConfigDraft", "BusinessConfigDiff"],
    }),
    dismissBusinessConfigProposal: builder.mutation({
      query: () => ({ url: "/draft/proposal", method: "DELETE" }),
      onQueryStarted: putDraft,
      invalidatesTags: ["BusinessConfigDraft"],
    }),
    // { fromVersion, draftRevision, changes, summary, validation }
    getBusinessConfigDiff: builder.query({
      query: () => "/diff",
      providesTags: ["BusinessConfigDiff"],
    }),
    // 200 { ok, version, steps, warnings } | 409 | 422 | 423 | 500
    publishBusinessConfig: builder.mutation({
      query: ({ ifRevision, note } = {}) => ({
        url: "/publish",
        method: "POST",
        body: { ifRevision, note: note ?? "" },
      }),
      invalidatesTags: TAGS,
    }),
    // Phase C5: { version, source: "record" | "none", count, templates } the
    // live record's composer templates (djr-be GET
    // /admin/business-config/composer-templates). The composer keeps its
    // built-in list unless source is "record" (composerTemplates.js).
    getBusinessConfigComposerTemplates: builder.query({
      query: () => "/composer-templates",
      providesTags: ["BusinessConfigComposerTemplates"],
    }),
    // Same answer as publish; 404 when the version is not in the DB.
    rollbackBusinessConfig: builder.mutation({
      query: ({ version, note } = {}) => ({
        url: `/rollback/${encodeURIComponent(String(version ?? ""))}`,
        method: "POST",
        body: { note: note ?? "" },
      }),
      invalidatesTags: TAGS,
    }),
  }),
});

export const {
  useGetBusinessConfigCurrentQuery,
  useGetBusinessConfigStatusQuery,
  useGetBusinessConfigVersionsQuery,
  useGetBusinessConfigVersionQuery,
  useGetBusinessConfigDraftQuery,
  usePatchBusinessConfigDraftMutation,
  useAcceptBusinessConfigProposalMutation,
  useDismissBusinessConfigProposalMutation,
  useGetBusinessConfigDiffQuery,
  useGetBusinessConfigComposerTemplatesQuery,
  usePublishBusinessConfigMutation,
  useRollbackBusinessConfigMutation,
} = businessConfigApi;

export default businessConfigApi;
export { businessConfigApi };
